/**
 * Barcode Service for WMS
 * Handles parsing and generation of Code128 barcode data
 */

export type BarcodeType = "BATCH" | "LOC" | "PROD" | "PALLET" | "UNKNOWN";

export interface ParsedBarcode {
  type: BarcodeType;
  code: string;
  raw: string;
  isValid: boolean;
}

/**
 * Parse a scanned barcode string
 * Supported formats:
 * - BATCH:{batch_number} - Production batch barcode
 * - LOC:{full_code} - Position/location barcode
 * - PROD:{sku} - Product barcode (for future use)
 * - PALLET:{code} - Pallet barcode
 */
export function parseBarcode(rawBarcode: string): ParsedBarcode {
  const trimmed = rawBarcode.trim();

  // Check BATCH format: BATCH:{batch_number}
  if (trimmed.startsWith("BATCH:")) {
    const code = trimmed.substring(6); // Remove "BATCH:" prefix
    return {
      type: "BATCH",
      code,
      raw: trimmed,
      isValid: code.length > 0,
    };
  }

  // Check LOC format: LOC:{full_code}
  if (trimmed.startsWith("LOC:")) {
    const code = trimmed.substring(4); // Remove "LOC:" prefix
    return {
      type: "LOC",
      code,
      raw: trimmed,
      isValid: code.length > 0,
    };
  }

  // Check PROD format: PROD:{sku}
  if (trimmed.startsWith("PROD:")) {
    const code = trimmed.substring(5); // Remove "PROD:" prefix
    return {
      type: "PROD",
      code,
      raw: trimmed,
      isValid: code.length > 0,
    };
  }

  // Check PALLET format: PALLET:{code}
  if (trimmed.startsWith("PALLET:")) {
    const code = trimmed.substring(7); // Remove "PALLET:" prefix
    return {
      type: "PALLET",
      code,
      raw: trimmed,
      isValid: code.length > 0,
    };
  }

  // Unknown format - return as-is
  return {
    type: "UNKNOWN",
    code: trimmed,
    raw: trimmed,
    isValid: false,
  };
}

/**
 * Generate barcode data for a production batch
 */
export function generateBatchBarcode(batchNumber: string): string {
  return `BATCH:${batchNumber}`;
}

/**
 * Generate barcode data for a position/location
 */
export function generatePositionBarcode(fullCode: string): string {
  return `LOC:${fullCode}`;
}

/**
 * Generate barcode data for a product
 */
export function generateProductBarcode(sku: string): string {
  return `PROD:${sku}`;
}

/**
 * Generate barcode data for a pallet
 */
export function generatePalletBarcode(palletCode: string): string {
  return `PALLET:${palletCode}`;
}

/**
 * Validate that a scanned barcode matches expected type
 */
export function validateBarcodeType(
  rawBarcode: string,
  expectedType: BarcodeType
): { valid: boolean; parsed: ParsedBarcode; error?: string } {
  const parsed = parseBarcode(rawBarcode);

  if (!parsed.isValid) {
    return {
      valid: false,
      parsed,
      error: "Formato de codigo de barras invalido",
    };
  }

  if (parsed.type !== expectedType) {
    const typeLabels: Record<BarcodeType, string> = {
      BATCH: "Lote",
      LOC: "Ubicacion",
      PROD: "Producto",
      PALLET: "Pallet",
      UNKNOWN: "Desconocido",
    };
    return {
      valid: false,
      parsed,
      error: `Tipo incorrecto. Esperado: ${typeLabels[expectedType]}, Recibido: ${typeLabels[parsed.type]}`,
    };
  }

  return { valid: true, parsed };
}
